import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import { Link } from "react-router-dom";
import Shell from "../Shell";
import { authHeaders } from "../api";

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (String(dt) === "Invalid Date") return d;
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function BulkAttendanceModal({
  open,
  group,
  onClose,
  members,
  membersLoading,
  memberSearch,
  setMemberSearch,
  selectedUserIds,
  setSelectedUserIds,
  mode,
  setMode,
  selectedDates,
  setSelectedDates,
  pendingDate,
  setPendingDate,
  rangeStart,
  setRangeStart,
  rangeEnd,
  setRangeEnd,
  saving,
  onSubmit,
}) {
  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      `${m.name || ""}`.toLowerCase().includes(q)
    );
  }, [members, memberSearch]);

  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserIds.map((x) => Number(x))),
    [selectedUserIds]
  );

  const toggleUser = (userId) => {
    const id = Number(userId);
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllFiltered = () => {
    setSelectedUserIds((prev) => {
      const set = new Set(prev.map(Number));
      filteredMembers.forEach((m) => set.add(Number(m.user_id)));
      return Array.from(set);
    });
  };

  const clearAllUsers = () => setSelectedUserIds([]);

  const addPendingDate = () => {
    if (!pendingDate) return;
    setSelectedDates((prev) => {
      if (prev.includes(pendingDate)) return prev;
      return [...prev, pendingDate].sort();
    });
    setPendingDate("");
  };

  const removeDate = (date) => {
    setSelectedDates((prev) => prev.filter((d) => d !== date));
  };

  const clearDates = () => setSelectedDates([]);

  if (!open || !group) return null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={onClose}
    >
      <div
        className="modal"
        onMouseDown={(e) => e.stopPropagation()}
        style={{ width: "min(1100px, 96vw)", maxHeight: "90vh" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Skrá mætingu</div>
            <div style={{ opacity: 0.75, marginTop: 4 }}>
              Hópur: <strong>{group.name}</strong>
            </div>
          </div>

          <button className="btn ghost sm" onClick={onClose}>
            Loka
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1.2fr) minmax(280px, 1fr)", gap: 18 }}>
          {/* Left: users */}
          <div
            style={{
              border: "1px solid #d7dbe3",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              minWidth: 0,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Velja notendur</div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Leita að nafni"
                style={{
                  flex: "1 1 220px",
                  minWidth: 0,
                  padding: "8px 10px",
                  border: "1px solid #d7dbe3",
                  borderRadius: 8,
                }}
              />
              <button className="btn ghost sm" type="button" onClick={selectAllFiltered}>
                Velja alla sýnda
              </button>
              <button className="btn ghost sm" type="button" onClick={clearAllUsers}>
                Hreinsa val
              </button>
            </div>

            <div style={{ opacity: 0.8, marginBottom: 10 }}>
              Valdir notendur: {selectedUserIds.length}
            </div>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                maxHeight: 420,
                overflow: "auto",
                background: "#fafafa",
              }}
            >
              {membersLoading ? (
                <div style={{ padding: 16 }}>Hleð notendum…</div>
              ) : filteredMembers.length === 0 ? (
                <div style={{ padding: 16 }}>
                  Engir notendur fundust fyrir þennan hóp.
                </div>
              ) : (
                filteredMembers.map((m) => (
                  <label
                    key={m.user_id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "24px minmax(0,1fr) auto auto",
                      gap: 10,
                      alignItems: "center",
                      padding: "10px 12px",
                      borderBottom: "1px solid #eceff3",
                      cursor: "pointer",
                      background: selectedUserIdSet.has(Number(m.user_id)) ? "#eef5ff" : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIdSet.has(Number(m.user_id))}
                      onChange={() => toggleUser(m.user_id)}
                    />
                    <span style={{ minWidth: 0, wordBreak: "break-word" }}>{m.name}</span>
                    <span style={{ opacity: 0.75, whiteSpace: "nowrap" }}>
                      Mæting: {m.count ?? 0}
                    </span>
                    <span style={{ opacity: 0.75, whiteSpace: "nowrap" }}>
                      Síðast: {fmtDate(m.last)}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Right: dates */}
          <div
            style={{
              border: "1px solid #d7dbe3",
              borderRadius: 12,
              padding: 14,
              background: "#fff",
              minWidth: 0,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Velja daga</div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <button
                type="button"
                className={`btn sm ${mode === "dates" ? "" : "ghost"}`}
                onClick={() => setMode("dates")}
              >
                Valdir dagar
              </button>
              <button
                type="button"
                className={`btn sm ${mode === "range" ? "" : "ghost"}`}
                onClick={() => setMode("range")}
              >
                Tímabil
              </button>
            </div>

            {mode === "dates" ? (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                  <input
                    type="date"
                    value={pendingDate}
                    onChange={(e) => setPendingDate(e.target.value)}
                  />
                  <button className="btn sm" type="button" onClick={addPendingDate}>
                    Bæta við degi
                  </button>
                  <button className="btn ghost sm" type="button" onClick={clearDates}>
                    Hreinsa daga
                  </button>
                </div>

                <div style={{ opacity: 0.8, marginBottom: 10 }}>
                  Valdir dagar: {selectedDates.length}
                </div>

                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    minHeight: 160,
                    maxHeight: 320,
                    overflow: "auto",
                    background: "#fafafa",
                    padding: 10,
                  }}
                >
                  {selectedDates.length === 0 ? (
                    <div style={{ opacity: 0.7 }}>Engir dagar valdir.</div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {selectedDates.map((d) => (
                        <div
                          key={d}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 10px",
                            borderRadius: 999,
                            border: "1px solid #d7dbe3",
                            background: "#fff",
                          }}
                        >
                          <span>{fmtDate(d)}</span>
                          <button
                            type="button"
                            className="btn ghost sm"
                            onClick={() => removeDate(d)}
                          >
                            Fjarlægja
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gap: 12 }}>
                  <div>
                    <div style={{ opacity: 0.75, marginBottom: 4 }}>Frá</div>
                    <input
                      type="date"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <div style={{ opacity: 0.75, marginBottom: 4 }}>Til</div>
                    <input
                      type="date"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            <div
              style={{
                marginTop: 18,
                paddingTop: 14,
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button className="btn ghost sm" type="button" onClick={onClose} disabled={saving}>
                Hætta við
              </button>
              <button className="btn sm" type="button" onClick={onSubmit} disabled={saving}>
                {saving ? "Vista…" : "Skrá mætingu"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GroupsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("name");

  const [role, setRole] = useState("staff");
  const isAdmin = role === "admin";

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [mode, setMode] = useState("dates"); // dates | range
  const [selectedDates, setSelectedDates] = useState([]);
  const [pendingDate, setPendingDate] = useState(todayIso());
  const [rangeStart, setRangeStart] = useState(todayIso());
  const [rangeEnd, setRangeEnd] = useState(todayIso());
  const [savingBulk, setSavingBulk] = useState(false);

  const dq = useDebounced(q, 350);

  const params = useMemo(() => {
    const p = {};
    if (dq.trim()) p.q = dq.trim();
    if (orderBy) p.order_by = orderBy;
    return p;
  }, [dq, orderBy]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/groups-list/", {
        params,
        headers: authHeaders(),
      });
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/user/role/", {
          headers: authHeaders(),
        });
        setRole(data?.role || "staff");
      } catch {
        setRole("staff");
      }
    })();
  }, []);

  const closeBulkModal = () => {
    setBulkOpen(false);
    setBulkGroup(null);
    setGroupMembers([]);
    setMemberSearch("");
    setSelectedUserIds([]);
    setMode("dates");
    setSelectedDates([]);
    setPendingDate(todayIso());
    setRangeStart(todayIso());
    setRangeEnd(todayIso());
    setSavingBulk(false);
  };

  const openBulkModal = async (group) => {
    setBulkGroup(group);
    setBulkOpen(true);
    setMembersLoading(true);
    setGroupMembers([]);
    setMemberSearch("");
    setSelectedUserIds([]);
    setMode("dates");
    setSelectedDates([]);
    setPendingDate(todayIso());
    setRangeStart(todayIso());
    setRangeEnd(todayIso());

    try {
      const { data } = await api.get(`/api/groups/${group.id}/members/`, {
        headers: authHeaders(),
      });

      const members = (Array.isArray(data) ? data : []).map((m) => ({
        user_id: m.user_id,
        name: m.name,
        count: m.count,
        last: m.last,
      }));

      setGroupMembers(members);
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að sækja notendur í hóp.");
      setGroupMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const submitBulkAttendance = async () => {
    if (!bulkGroup?.id) {
      alert("Vantar hóp.");
      return;
    }

    if (selectedUserIds.length === 0) {
      alert("Veldu að minnsta kosti einn notanda.");
      return;
    }

    const payload = {
      user_ids: selectedUserIds,
    };

    if (mode === "dates") {
      if (selectedDates.length === 0) {
        alert("Veldu að minnsta kosti einn dag.");
        return;
      }
      payload.dates = selectedDates;
    } else {
      if (!rangeStart || !rangeEnd) {
        alert("Veldu bæði frá- og tildagsetningu.");
        return;
      }
      if (rangeEnd < rangeStart) {
        alert("Endadagsetning má ekki vera á undan upphafsdagsetningu.");
        return;
      }
      payload.start = rangeStart;
      payload.end = rangeEnd;
    }

    setSavingBulk(true);
    try {
      const { data } = await api.post(
        `/api/groups/${bulkGroup.id}/bulk_mark_attendance/`,
        payload,
        { headers: authHeaders() }
      );

      alert(
        `Mæting skráð.\nNýjar færslur: ${data?.created_count ?? 0}\nSleppt vegna tvítektar: ${data?.skipped_count ?? 0}`
      );

      closeBulkModal();
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Tókst ekki að skrá mætingu.");
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <Shell
      title="Hugarafl starfsfólk"
      rightMenu
      menuItems={[
        { title: "Yfirlit yfir félagsaðila", to: "/members" },
        { title: "Yfirlit yfir hópa", to: "/groups" },
        { title: "Skrá nýjan hóp", to: "/groups/new" },
      ]}
    >
      <h2 style={{ textAlign: "center", margin: "8px 0 18px" }}>Hópar</h2>

      <div className="members-toolbar">
        <div className="search-block">
          <label className="sr" htmlFor="search">Leita</label>
          <input
            id="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Leita eftir nafni eða hópstjóra"
          />
        </div>

        <div className="filter-block">
          <button className="btn sm" onClick={() => setFiltersOpen((v) => !v)}>
            Sía/röð
          </button>

          {filtersOpen && (
            <div className="filter-popover">
              <div className="filter-title" style={{ marginTop: 4 }}>Raða eftir:</div>
              <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)}>
                <option value="name">Heiti</option>
                <option value="count">Skráðar mætingar</option>
                <option value="last_held">Mætti síðast</option>
                <option value="leaders">Hópstjórar</option>
              </select>

              <div style={{ textAlign: "right", marginTop: 10 }}>
                <button className="btn ghost sm" onClick={() => setFiltersOpen(false)}>
                  Loka síu
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Hleð…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table zebra">
              <thead>
                <tr>
                  <th>Hópanfn</th>
                  <th>Hópstjórar</th>
                  <th>Skráðar mætingar</th>
                  <th>Mætti síðast</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 16 }}>
                      Engir hópar fundust
                    </td>
                  </tr>
                ) : (
                  rows.map((g) => (
                    <tr key={g.id}>
                      <td>{g.name}</td>
                      <td>{g.leaders || "—"}</td>
                      <td>{g.attendance_count}</td>
                      <td>{fmtDate(g.last_held)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                          <Link className="btn sm ghost" to={`/groups/${g.id}`}>
                            Nánar
                          </Link>

                          {isAdmin && (
                            <button
                              type="button"
                              className="btn sm"
                              onClick={() => openBulkModal(g)}
                            >
                              Skrá mætingu
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <BulkAttendanceModal
            open={bulkOpen}
            group={bulkGroup}
            onClose={closeBulkModal}
            members={groupMembers}
            membersLoading={membersLoading}
            memberSearch={memberSearch}
            setMemberSearch={setMemberSearch}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            mode={mode}
            setMode={setMode}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            pendingDate={pendingDate}
            setPendingDate={setPendingDate}
            rangeStart={rangeStart}
            setRangeStart={setRangeStart}
            rangeEnd={rangeEnd}
            setRangeEnd={setRangeEnd}
            saving={savingBulk}
            onSubmit={submitBulkAttendance}
          />
        </>
      )}
    </Shell>
  );
}